import { useState, useEffect } from 'react';
import { ActiveWorkOverview } from '@/components/features/ActiveWorkOverview';
import { RequestSubmissionPanel } from '@/components/features/RequestSubmissionPanel';
import { CapacityTracker } from '@/components/features/CapacityTracker';
import { DeliveryTimeline } from '@/components/features/DeliveryTimeline';
import { AssetExchange } from '@/components/features/AssetExchange';
import { ActivityFeed } from '@/components/features/ActivityFeed';
import { DeveloperVisibility } from '@/components/features/DeveloperVisibility';
import { PackageSummary } from '@/components/features/PackageSummary';
import { SprintProgress } from '@/components/features/SprintProgress';
import { WeeklyWorkSummary } from '@/components/features/WeeklyWorkSummary';
import { TaskDetailModal } from '@/components/features/TaskDetailModal';
import { NotificationsPanel } from '@/components/features/NotificationsPanel';
import { SettingsModal } from '@/components/features/SettingsModal';
import { AuthPage } from '@/components/features/AuthPage';
import { AdminDashboard } from '@/components/features/AdminDashboard';
import { Button } from '@/components/ui/Button';
import { LayoutDashboard, Plus, Bell, Settings, Menu, X, LogOut } from 'lucide-react';
import {
  tasksApi,
  agencyApi,
  filesApi,
  notificationsApi,
  settingsApi,
  authApi,
  developersApi,
  setSessionToken,
  clearSessionToken,
  getSessionToken,
} from '@/lib/api';
import type {
  Task,
  Agency,
  Developer,
  FileAsset,
  FileType,
  WeeklyWorkItem,
  SprintProgress as SprintProgressData,
  ProjectNotification,
  DashboardSettings,
  AuthUser,
  PasswordResetRequest,
} from '@/types';
import './App.css';

// Helper to transform API dates
const transformTaskDates = (task: Task): Task => ({
  ...task,
  estimatedCompletion: new Date(task.estimatedCompletion).toISOString(),
  createdAt: new Date(task.createdAt).toISOString(),
  updatedAt: new Date(task.updatedAt).toISOString(),
});

function App() {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [agency, setAgency] = useState<Agency | null>(null);
  const [developers, setDevelopers] = useState<Developer[]>([]);
  const [files, setFiles] = useState<FileAsset[]>([]);
  const [weeklyWork, setWeeklyWork] = useState<WeeklyWorkItem[]>([]);
  const [sprintProgress, setSprintProgress] = useState<SprintProgressData | null>(null);
  const [notifications, setNotifications] = useState<ProjectNotification[]>([]);
  const [settings, setSettings] = useState<DashboardSettings | null>(null);
  const [authUser, setAuthUser] = useState<AuthUser | null>(null);
  const [allUsers, setAllUsers] = useState<AuthUser[]>([]);
  const [pendingUsers, setPendingUsers] = useState<AuthUser[]>([]);
  const [passwordResetRequests, setPasswordResetRequests] = useState<PasswordResetRequest[]>([]);
  const [authLoading, setAuthLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [activeTab, setActiveTab] = useState<'dashboard' | 'new-request'>('dashboard');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    const restoreSession = async () => {
      if (!getSessionToken()) {
        setAuthLoading(false);
        setLoading(false);
        return;
      }

      try {
        setAuthUser(await authApi.me());
      } catch {
        clearSessionToken();
      } finally {
        setAuthLoading(false);
      }
    };

    restoreSession();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    setLoadError(null);

    try {
      const [
        tasksData,
        agencyData,
        filesData,
        weeklyWorkData,
        sprintProgressData,
        notificationsData,
        settingsData,
      ] = await Promise.all([
        tasksApi.getAll(),
        agencyApi.get(),
        filesApi.getAll(),
        agencyApi.getWeeklyWork(),
        agencyApi.getSprintProgress(),
        notificationsApi.getAll(),
        settingsApi.get(),
      ]);

      const transformedTasks = tasksData.map(transformTaskDates);
      setTasks(transformedTasks);
      setAgency(agencyData);
      setFiles(filesData);
      setWeeklyWork(weeklyWorkData);
      setSprintProgress(sprintProgressData);
      setNotifications(notificationsData);
      setSettings(settingsData);

      const devs = transformedTasks
        .map(t => t.assignedDeveloper)
        .filter((d): d is Developer => d !== null);
      const uniqueDevs = [...new Map(devs.map(d => [d.id, d])).values()];
      setDevelopers(uniqueDevs);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to load dashboard data';
      setLoadError(message);
    } finally {
      setLoading(false);
    }
  };

  const fetchAdminData = async () => {
    setLoading(true);
    setLoadError(null);

    try {
      const [
        pendingUsersData, passwordResetRequestsData, notificationsData,
        settingsData, tasksData, developersData, agencyData, allUsersData,
      ] = await Promise.all([
        authApi.pendingUsers(),
        authApi.passwordResetRequests(),
        notificationsApi.getAll(),
        settingsApi.get(),
        tasksApi.getAll(),
        developersApi.getAll(),
        agencyApi.get(),
        authApi.listUsers(),
      ]);

      setPendingUsers(pendingUsersData);
      setPasswordResetRequests(passwordResetRequestsData);
      setNotifications(notificationsData);
      setSettings(settingsData);
      setTasks(tasksData.map(transformTaskDates));
      setDevelopers(developersData);
      setAgency(agencyData);
      // store all users separately so we can show the full client list
      setPendingUsers(pendingUsersData); // already set above, keep pending list for approval actions
      setFiles(await filesApi.getAll().catch(() => []));
      // allUsers goes into its own state
      setAllUsers(allUsersData);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to load admin data';
      setLoadError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authUser) return;

    if (authUser.role === 'admin') {
      fetchAdminData();
      return;
    }

    fetchDashboardData();
  }, [authUser]);

  const refreshTasksAndFiles = async () => {
    const [tasksData, filesData, notificationsData] = await Promise.all([
      tasksApi.getAll(),
      filesApi.getAll(),
      notificationsApi.getAll(),
    ]);
    const transformedTasks = tasksData.map(transformTaskDates);
    setTasks(transformedTasks);
    setFiles(filesData);
    setNotifications(notificationsData);
    return transformedTasks;
  };

  const refreshNotifications = async () => {
    const notificationsData = await notificationsApi.getAll();
    setNotifications(notificationsData);
  };

  const getFileType = (file: File): FileType => {
    const name = file.name.toLowerCase();
    if (name.endsWith('.env') || name.includes('credential') || name.includes('secret')) return 'credentials';
    if (file.type.startsWith('image/') || name.endsWith('.fig') || name.endsWith('.sketch')) return 'design';
    if (name.includes('staging') || name.endsWith('.url') || name.endsWith('.webloc')) return 'staging';
    return 'deliverable';
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  const readFileAsBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = String(reader.result);
        resolve(result.includes(',') ? result.split(',')[1] : result);
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });

  const fileToUploadPayload = async (file: File, taskId?: string) => ({
    name: file.name,
    type: getFileType(file),
    size: formatFileSize(file.size),
    taskId,
    uploadedBy: authUser?.name || 'Client',
    contentBase64: await readFileAsBase64(file),
    contentType: file.type || 'application/octet-stream',
  });

  const handleNewRequest = async (formData: any) => {
    try {
      const createdTask = await tasksApi.create({
        title: formData.title,
        description: formData.description,
        requestType: formData.requestType,
        priority: formData.priority,
        agencyId: 'agency-1',
      });

      if (formData.files?.length) {
        await Promise.all(
          formData.files.map(async (file: File) =>
            filesApi.upload(await fileToUploadPayload(file, createdTask.id))
          )
        );
      }
      
      await refreshTasksAndFiles();
      setActiveTab('dashboard');
      setNotificationsOpen(true);
    } catch (error) {
      console.error('Error creating task:', error);
    }
  };

  const handleAssetUpload = async (uploadedFiles: FileList) => {
    try {
      await Promise.all(
        Array.from(uploadedFiles).map(async (file) =>
          filesApi.upload(await fileToUploadPayload(file))
        )
      );
      await refreshTasksAndFiles();
    } catch (error) {
      console.error('Error uploading file:', error);
    }
  };

  const handleTaskAction = async (taskId: string, action: string, data?: any) => {
    try {
      switch (action) {
        case 'approve':
          await tasksApi.approve(taskId, data?.author);
          break;
        case 'revision':
          await tasksApi.requestRevision(taskId, data);
          break;
        case 'comment':
          await tasksApi.addComment(taskId, data);
          break;
      }
      
      const transformedTasks = await refreshTasksAndFiles();
      
      const updatedTask = transformedTasks.find((t) => t.id === taskId);
      if (updatedTask) setSelectedTask(updatedTask);
    } catch (error) {
      console.error('Error handling task action:', error);
    }
  };

  const handleFileDownload = async (file: FileAsset) => {
    try {
      const blob = await filesApi.download(file.id);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = file.name;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  const handleMarkNotificationRead = async (id: string) => {
    try {
      await notificationsApi.markRead(id);
      await refreshNotifications();
    } catch (error) {
      console.error('Error marking notification read:', error);
    }
  };

  const handleMarkAllNotificationsRead = async () => {
    try {
      const updatedNotifications = await notificationsApi.markAllRead();
      setNotifications(updatedNotifications);
    } catch (error) {
      console.error('Error marking notifications read:', error);
    }
  };

  const handleSaveSettings = async (nextSettings: DashboardSettings) => {
    setSavingSettings(true);
    try {
      const savedSettings = await settingsApi.update(nextSettings);
      setSettings(savedSettings);
      setSettingsOpen(false);
    } catch (error) {
      console.error('Error saving settings:', error);
    } finally {
      setSavingSettings(false);
    }
  };

  const handleAuthenticated = (token: string, user: AuthUser) => {
    setSessionToken(token);
    setAuthUser(user);
  };

  const handleLogout = async () => {
    try {
      await authApi.logout();
    } catch {
      // Session cleanup should still happen locally.
    } finally {
      clearSessionToken();
      setAuthUser(null);
      setTasks([]);
      setFiles([]);
      setNotifications([]);
      setPendingUsers([]);
      setPasswordResetRequests([]);
      setAllUsers([]);
      setDevelopers([]);
      setAgency(null);
      setLoading(false);
    }
  };

  const handleAdminTaskAction = async (taskId: string, action: string, data?: any) => {
    try {
      switch (action) {
        case 'updateStatus':
          await tasksApi.updateStatus(taskId, data.status);
          break;
        case 'assignDeveloper':
          await tasksApi.update(taskId, { developerId: data.developerId });
          break;
        case 'editTask':
          await tasksApi.update(taskId, data);
          break;
        case 'deleteTask':
          await tasksApi.delete(taskId);
          break;
        case 'approve':
          await tasksApi.approve(taskId, data?.author);
          break;
        case 'revision':
          await tasksApi.requestRevision(taskId, data);
          break;
        case 'comment':
          await tasksApi.addComment(taskId, data);
          break;
      }
      await fetchAdminData();
    } catch (error) {
      console.error('Admin task action failed:', error);
    }
  };

  const handleApproveUser = async (id: string) => {
    await authApi.approveUser(id);
    await fetchAdminData();
  };

  const handleApprovePasswordReset = async (id: string) => {
    const approvedRequest = await authApi.approvePasswordResetRequest(id);
    setPasswordResetRequests((requests) =>
      requests.map((request) => (request.id === id ? approvedRequest : request))
    );
    const notificationsData = await notificationsApi.getAll();
    setNotifications(notificationsData);
  };

  const handleCreateAdminAlert = async (data: { title: string; message: string }) => {
    await notificationsApi.create(data);
    await fetchAdminData();
  };

  const handleCreateDeveloper = async (data: any) => {
    await developersApi.create(data);
    await fetchAdminData();
  };

  const handleUpdateDeveloper = async (id: string, data: any) => {
    await developersApi.update(id, data);
    await fetchAdminData();
  };

  const handleDeleteDeveloper = async (id: string) => {
    await developersApi.delete(id);
    await fetchAdminData();
  };

  const handleUpdateAgency = async (data: any) => {
    await agencyApi.update(data);
    await fetchAdminData();
  };

  const handleUpdateUserRole = async (id: string, role: 'admin' | 'client') => {
    await authApi.updateUserRole(id, role);
    await fetchAdminData();
  };

  const handleDeleteUser = async (id: string) => {
    await authApi.deleteUser(id);
    await fetchAdminData();
  };

  const unreadCount = notifications.filter((notification) => !notification.read).length;

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Checking session...</p>
        </div>
      </div>
    );
  }

  if (!authUser) {
    return <AuthPage onAuthenticated={handleAuthenticated} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-6 text-center shadow-sm">
          <h1 className="text-xl font-bold text-slate-900">Dashboard unavailable</h1>
          <p className="mt-2 text-sm text-slate-600">{loadError}</p>
          <Button className="mt-6" onClick={authUser.role === 'admin' ? fetchAdminData : fetchDashboardData}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (authUser.role === 'admin') {
    return (
      <AdminDashboard
        user={authUser}
        tasks={tasks}
        allUsers={allUsers}
        pendingUsers={pendingUsers}
        passwordResetRequests={passwordResetRequests}
        notifications={notifications}
        developers={developers}
        agency={agency}
        onLogout={handleLogout}
        onApproveUser={handleApproveUser}
        onApprovePasswordReset={handleApprovePasswordReset}
        onCreateAlert={handleCreateAdminAlert}
        onTaskAction={handleAdminTaskAction}
        onCreateDeveloper={handleCreateDeveloper}
        onUpdateDeveloper={handleUpdateDeveloper}
        onDeleteDeveloper={handleDeleteDeveloper}
        onUpdateAgency={handleUpdateAgency}
        onUpdateUserRole={handleUpdateUserRole}
        onDeleteUser={handleDeleteUser}
      />
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
                <span className="text-white text-xl font-bold">O</span>
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-900">OrbitCrew</h1>
                <p className="text-xs text-slate-500">Agency Dashboard</p>
              </div>
            </div>

            <div className="hidden md:flex items-center gap-4">
              <Button
                variant={activeTab === 'dashboard' ? 'primary' : 'ghost'}
                onClick={() => setActiveTab('dashboard')}
                leftIcon={<LayoutDashboard className="w-4 h-4" />}
              >
                Dashboard
              </Button>
              <Button
                variant={activeTab === 'new-request' ? 'primary' : 'ghost'}
                onClick={() => setActiveTab('new-request')}
                leftIcon={<Plus className="w-4 h-4" />}
              >
                New Request
              </Button>
              <Button
                variant="ghost"
                onClick={handleLogout}
                leftIcon={<LogOut className="w-4 h-4" />}
              >
                Sign out
              </Button>
            </div>

            <div className="flex items-center gap-3">
              <div className="relative">
                <button
                  type="button"
                  aria-label="Open project alerts"
                  onClick={() => {
                    setNotificationsOpen((open) => !open);
                    setSettingsOpen(false);
                  }}
                  className="relative p-2 text-slate-500 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>

                {notificationsOpen && (
                  <NotificationsPanel
                    notifications={notifications}
                    onClose={() => setNotificationsOpen(false)}
                    onMarkRead={handleMarkNotificationRead}
                    onMarkAllRead={handleMarkAllNotificationsRead}
                  />
                )}
              </div>
              <button
                type="button"
                aria-label="Open dashboard settings"
                onClick={() => {
                  setSettingsOpen(true);
                  setNotificationsOpen(false);
                }}
                className="p-2 text-slate-500 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <Settings className="w-5 h-5" />
              </button>
              <button
                className="md:hidden p-2 text-slate-500 hover:bg-slate-100 rounded-lg"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden border-t border-slate-200 px-4 py-3 space-y-2">
            <Button
              variant={activeTab === 'dashboard' ? 'primary' : 'ghost'}
              onClick={() => { setActiveTab('dashboard'); setMobileMenuOpen(false); }}
              className="w-full justify-start"
              leftIcon={<LayoutDashboard className="w-4 h-4" />}
            >
              Dashboard
            </Button>
            <Button
              variant={activeTab === 'new-request' ? 'primary' : 'ghost'}
              onClick={() => { setActiveTab('new-request'); setMobileMenuOpen(false); }}
              className="w-full justify-start"
              leftIcon={<Plus className="w-4 h-4" />}
            >
              New Request
            </Button>
            <Button
              variant="ghost"
              onClick={() => { void handleLogout(); setMobileMenuOpen(false); }}
              className="w-full justify-start"
              leftIcon={<LogOut className="w-4 h-4" />}
            >
              Sign out
            </Button>
          </div>
        )}
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'new-request' ? (
          <div className="max-w-3xl mx-auto">
            <RequestSubmissionPanel onSubmit={handleNewRequest} />
          </div>
        ) : (
          <div className="space-y-6">
            <WeeklyWorkSummary weeklyWork={weeklyWork} />

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
              <div className="xl:col-span-2 space-y-6">
                <ActiveWorkOverview
                  tasks={tasks}
                  onTaskClick={setSelectedTask}
                  onNewRequest={() => setActiveTab('new-request')}
                />
                <DeliveryTimeline tasks={tasks} />
                <AssetExchange files={files} onUpload={handleAssetUpload} onDownload={handleFileDownload} />
              </div>

              <div className="space-y-6">
                {agency && <CapacityTracker agency={agency} />}
                <DeveloperVisibility developers={developers} />
                <ActivityFeed tasks={tasks} />
                {agency && <PackageSummary agency={agency} />}
                {sprintProgress && <SprintProgress sprint={sprintProgress} />}
              </div>
            </div>
          </div>
        )}
      </main>

      {selectedTask && (
        <TaskDetailModal
          task={selectedTask}
          onClose={() => setSelectedTask(null)}
          onAction={handleTaskAction}
        />
      )}

      {settingsOpen && settings && (
        <SettingsModal
          settings={settings}
          isSaving={savingSettings}
          onClose={() => setSettingsOpen(false)}
          onSave={handleSaveSettings}
        />
      )}
    </div>
  );
}

export default App;
